import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';

import { ProjectService } from '../../../services/project.service';
import { ProjectModel } from '../../../services/project.model.service';
import { UserService } from '../../../services/core.user.service';
import { UiService } from '../../../services/core.ui.service';
import { GfTranslateService } from '../../../services/core.translate.service';
import { MenuService } from '../../../../widgets/mega-menu/mega-menu.service.component';
import { WikiService } from '../../../../widgets/wiki/services/wiki.service';
import { WikiHelperService } from '../../../../widgets/wiki/services/wiki.helper';
import { StateService } from '@uirouter/angular';
import { GfTitleService } from '../../../services/core.title.service';
import { GfMetaService } from '../../../services/core.meta.service';

@Component({
    selector: 'project-home',
    template: require('./project.home.html')
})
export class ProjectHomeComponent implements OnInit, OnDestroy, OnChanges {
    @Input() project: any;
    @Input() projectMenu: any;
    @Input() isProjectAdmin: boolean = false;

    // Component properties
    newProjectDescription: string;
    editDescription: boolean = false;
    details: any = false;
    tracker_stats: boolean = false;
    trackerStatsData: any = false;
    hasPermissions: boolean = false;
    hasUser: boolean = false;
    pageName: string;
    inputs: any;
    loading: boolean = false;
    usedCategories: any[];
    isProjectMember: any = false;
    showCategories: boolean = false;
    miniMenuProject: any = false;
    miniLoading: any = false;
    org: any;
    useCustomHomepage: string = 'N';
    homepageResolved: boolean = false;
    wikiHtml: string = '';

    // Computed properties
    isOrg: boolean = false;
    isPending: boolean = false;
    isActive: boolean = false;
    isDeleted: boolean = false;
    scmPluginType: any = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private projectService: ProjectService,
        private projectModel: ProjectModel,
        private userService: UserService,
        private uiService: UiService,
        private translateService: GfTranslateService,
        private stateService: StateService,
        private menuService: MenuService,
        private wikiService: WikiService,
        private wikiHelper: WikiHelperService,
        private http: HttpClient,
        private window: Window,
        private titleService: GfTitleService,
        private metaService: GfMetaService
    ) {}

    ngOnInit() {
        if (!this.project || !this.project.id || !this.project.unixName) {
            console.error('Project data is not available');
            return;
        }

        this.newProjectDescription = this.project.description || '';
        this.editDescription = false;

        this.titleService.setTitle(this.project.name || 'Unknown Project', 'Home');

        this.setSeoMetadata();

        this.details = false;
        this.loading = false;
        this.tracker_stats = false;
        this.trackerStatsData = false;
        this.hasPermissions = (this.window as any).hasProjectPermissions ? true : false;
        this.hasUser = this.userService.session.hasUser();

        this.pageName = _.get(this.stateService.params, 'pagename', 'ProjectHome');

        this.refreshInputs();

        this.setFlags();

        // Initialize useCustomHomepage to 'N' initially, will be updated by dataFetch.
        // homepageResolved stays false until getUseCustomSplash settles, so the
        // template shows a spinner instead of briefly flashing the default homepage.
        this.useCustomHomepage = 'N';
        this.homepageResolved = false;

        this.dataFetch();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.project || changes.projectMenu) {
            this.refreshInputs();
        }
    }

    /**
     * Emit per-page SEO metadata for the public project home page: meta
     * description, canonical URL, Open Graph tags and JSON-LD structured
     * data. Falls back to the project name when no description is set.
     */
    private setSeoMetadata() {
        const unixName = this.project.unixName;
        const projectName = this.project.name || 'Unknown Project';
        const description = this.project.description || projectName;
        const canonicalUrl = `${this.window.location.origin}/project/${unixName}`;

        this.metaService.setDescription(description);
        this.metaService.setCanonicalUrl(canonicalUrl);
        this.metaService.setOpenGraph({
            title: projectName,
            description: description,
            url: canonicalUrl
        });
        this.metaService.setJsonLd({
            '@context': 'https://schema.org',
            '@type': 'SoftwareSourceCode',
            name: projectName,
            description: description,
            url: canonicalUrl
        });
    }

    moduleAvailable(moduleName: string): boolean {
        return _.includes(_.get(this, 'projectMenu.modules', []), _.lowerCase(moduleName)) &&
            (_.get(this.projectMenu, _.lowerCase(moduleName), 0) > 0);
    }

    private refreshInputs() {
        const availableModules = {
            tracker: this.moduleAvailable('tracker'),
            build: this.moduleAvailable('build')
        };

        this.inputs = Object.assign({}, this.inputs || {}, {
            project: this.project,
            details: this.details,
            availableModules
        });
    }

    // Public Methods
    editProjectDescription(currentDescription: string) {
        this.newProjectDescription = currentDescription;
        this.editDescription = true;
    }

    saveProjectDescription(newDescription: string) {
        const update = {
            description: newDescription
        };
        
        const unixName = this.stateService.params.unixName;
        this.projectService.save(unixName, update)
            .then(
                (result) => {
                    this.uiService.message.showSuccess(
                        this.translateService.translate("Updated the Project Description successfully."),
                        "Project Description"
                    );
                    this.project.description = (result as any).description;
                },
                (failure) => {
                    console.trace(failure);
                    this.uiService.message.showWarning(
                        this.translateService.translate("Sorry, could not update the Project Description."),
                        "Project Description"
                    );
                }
            ).finally(
                () => {
                    this.editDescription = false;
                    this.newProjectDescription = null;
                }
            );
    }

    cancelEditDescription() {
        this.newProjectDescription = this.project.description;
        this.editDescription = false;
    }

    toggleShowCategories() {
        this.showCategories = !this.showCategories;
        (($ as any)('#categories')).collapse('toggle');
    }

    gotoCategory(category: any) {
        if (category.categories.length == 1) {
            this.stateService.go('home.project.trove', { selected: category.categories[0].id });
        }
        else {
            this.toggleShowCategories();
        }
    }

    showProjectMiniMenu(unixName: string) {
        // Toggle display if currently shown
        if (_.get(this, 'miniMenuProject.unixName', '') == unixName) {
            this.miniMenuProject = false;
            return false;
        }

        this.miniLoading = unixName;

        this.menuService.getProjectMenu(unixName).then(
            (pm) => {
                this.miniMenuProject = pm;
            }
        ).finally(() => { this.miniLoading = false; });
    }

    // Internal Methods
    private setFlags() {
        this.isOrg = this.projectModel.isOrg(this.project) ||
                this.projectModel.isOrg(this.details);

        this.isPending = _.get(this.project, 'status', 0) == 0;
        this.isActive = _.get(this.project, 'status', 0) == 1;
        this.isDeleted = _.get(this.project, 'status', 0) > 1;

        const first = _.find(
            _.get(this, 'project.rel.plugins', []),
            (p) => {
                return (p.indexOf('scm') === 0);
            }
        );

        this.scmPluginType = first ? first.replace('scm', '') : false;
    }

    private dataFetch() {
        this.loading = true;
        const unixName = this.stateService.params.unixName;

        const trove = this.projectService.trove(unixName).then((data: any) => {
            this.usedCategories = data.items;
        });

        // Our custom update check for project activity feed and tracker aggregates/table view
        const member = this.projectModel.isProjectMember(this.userService.session.getUnixName(), this.project.unixName)
        .then((response: any) => {
            this.isProjectMember = response;
            (this.window as any).hasProjectPermissions = response || this.project.isPublic == true || this.project.isPublic == 'Y';
            this.hasPermissions = response || this.project.isPublic == true || this.project.isPublic == 'Y';
        }, (response) => {
            this.isProjectMember = false;
            if (this.project.isPublic == true || this.project.isPublic == 'Y') {
                this.hasPermissions = true;
                (this.window as any).hasProjectPermissions = true;
            }
        });

        const details = this.projectService.details(unixName, { rel: 'trackers,activity_log,member_counts,org_metrics' })
        .then(
            (details) => {
                this.details = details;
                this.refreshInputs();
                this.setupOrg();
            },
            () => {
                const msg = this.translateService.translate('There was a problem loading project data for {{unixName}}. You can' +
                    'keep working but the summary view may not be accurate', { 'unixName': unixName });
                this.uiService.message.showWarning(msg, 'Data Error');
            }
        );

        this.wikiHtml = '';
        const splash = this.projectService.getUseCustomSplash(this.project.id)
            .then((response: any) => {
                this.useCustomHomepage = _.get(response, 'settingValue', 'N');
                this.homepageResolved = true;

                if (this.useCustomHomepage == 'Y') {
                    const pageName = 'ProjectHome';
                    const urlFn = (pagename) => {
                        const ret = this.stateService.href(
                            'home.project.summary.wiki.page',
                            { unixName: unixName, pagename: pagename }
                        );
                        return ret;
                    };
    
                    this.wikiService.info(pageName, {
                        rel: 'options,version,content',
                        project: this.project.unixName
                    })
                    .then((data: any) => {
                        const wikiContent = _.get(data, 'rel.content.data', '');
                        this.wikiHtml = this.wikiHelper.convertWikiWords(
                            wikiContent,
                            urlFn
                        );
                    });
                }
            }, () => { this.useCustomHomepage = 'N'; this.homepageResolved = true; });

        Promise.all([
            trove, member, details, splash
        ]).then(
            () => { 
                this.loading = false;
            }
        );
    }

    private setupOrg() {
        if (this.isOrg) {
            this.org = {
                topWiki: false,
                rootFolders: false,
                childProjects: false,
                projectFilter: { name: '' },
                resourceSearch: '',
                resourceResults: false
            };

            const childrenUrl = '/api/project?&page_size=999&inRootPath=' + this.project.id;
            this.http.get(childrenUrl).subscribe(
                (result: any) => {
                    this.org.childProjects = _.get(result, 'items', []);
                },
                (failure) => {
                    console.log(failure);
                    this.org.childProjects = [];
                }
            );

            const wikiUrl = '/api/wiki?sort_field=pageViews&sort_dir=desc&page_size=5&project=' + this.project.unixName;
            this.http.get(wikiUrl).subscribe(
                (result: any) => {
                    this.org.topWiki = _.get(result, 'items', []);
                },
                (failure) => {
                    console.log(failure);
                    this.org.topWiki = [];
                }
            );

            const unixName = this.stateService.params.unixName;
            const folderUrl = '/api/docman/' + unixName + '/folders?sort_field=folderName&sort_dir=asc&page_size=5&root=1';
            this.http.get(folderUrl).subscribe(
                (result: any) => {
                    this.org.rootFolders = _.filter(
                        _.get(result, 'items', []),
                        (fld) => {
                            return _.get(fld, 'docCount', 0) > 0;
                        }
                    );
                },
                (failure) => {
                    console.log(failure);
                    this.org.rootFolders = [];
                }
            );
        }
    }
}
